// The profile's rules as pure functions (docs/SPEC.md "Profile sync"): what the server's items plus this device's
// queued changes mean, and the changes one tap makes. Port of web/profile.js; same rules as backend/nowish/profile.py
// and ios NowishKit/Profile.swift. Nothing here touches the page, the network or storage (see sync.ts).
import { SHARED } from "../shared/config";
import { RULES, type HideSets, type NowishEvent } from "../shared/rules";

export type Kind = "tag-levels" | "stars" | "hidden-events" | "free";
/** A tag has one level: interest (★) or hidden (the eye-slash). No level = not set. */
export type TagLevel = "interest" | "hidden";

/** One profile row as the server stores it. `value`: a tag's level, or for "free" until-when in ms (null = until turned off). */
export interface Item { kind: Kind; key: string; on: boolean; at: number; value?: string | null }
/** The server's copy of an event (as sent, times as text): kept for starred / hidden events the map doesn't hold. */
export type RawEvent = Record<string, unknown> & { id: string; start: string };

export interface ProfileData {
  /** "kind|key" → the item as the server last sent it. */
  items: Record<string, Item>;
  /** Changes not yet acknowledged by the server, oldest first. */
  outbox: Item[];
  /** id → the server's copy of each starred / hidden event. */
  events: Record<string, RawEvent>;
}

export interface FreeState { on: boolean; until: number | null }
export interface ProfileView {
  stars: Set<string>;
  hiddenEvents: Set<string>;
  /** tag → level, for tags that have one. */
  levels: Map<string, TagLevel>;
  /** Interests (every tag whose level is "interest"). */
  interests: Set<string>;
  hiddenTags: Set<string>;
  free: FreeState;
}

export const emptyData = (): ProfileData => ({ items: {}, outbox: [], events: {} });

export const normTag = (t: string) => String(t).trim().toLowerCase();
/** Gone everywhere; the server never stores them either. */
export const RETIRED_TAGS: ReadonlySet<string> = new Set(SHARED.retiredTags);

export const itemId = (i: Pick<Item, "kind" | "key">) => `${i.kind}|${i.key}`;

/** The server's items with the queued changes on top (the newest change to an item wins), as sets. */
export function profileView(data: ProfileData, now: number): ProfileView {
  const merged: Record<string, Item> = { ...data.items };
  for (const c of data.outbox) {
    const id = itemId(c);
    const have = merged[id];
    if (!have || have.at <= c.at) merged[id] = c;
  }
  const v: ProfileView = {
    stars: new Set(), hiddenEvents: new Set(), levels: new Map(), interests: new Set(), hiddenTags: new Set(),
    free: { on: false, until: null },
  };
  for (const it of Object.values(merged).sort((a, b) => a.at - b.at)) {
    if (it.kind === "free") {
      const until = it.value ? Number(it.value) : null;
      v.free = { on: it.on && (until == null || until > now), until };
    } else if (it.kind === "tag-levels") {
      if (it.on && !RETIRED_TAGS.has(it.key) && (it.value === "interest" || it.value === "hidden")) v.levels.set(it.key, it.value);
    } else if (it.on) {
      (it.kind === "stars" ? v.stars : v.hiddenEvents).add(it.key);
    }
  }
  for (const [t, level] of v.levels) (level === "hidden" ? v.hiddenTags : v.interests).add(t);
  return v;
}

/**
 * The changes one tap makes, stamped: later than anything queued or already on the server (so two taps in the same
 * millisecond, or a clock that stepped back, still keep their order), plus the consistency rule the server also
 * applies: an event can't be both starred and hidden. Retired tags make no change.
 */
export function makeChanges(data: ProfileData, kind: Kind, key: string, on: boolean, value: string | null, now: number): Item[] {
  if (kind === "tag-levels") {
    key = normTag(key);
    if (RETIRED_TAGS.has(key)) return [];
  }
  const last = Math.max(0, ...data.outbox.map((c) => c.at), ...Object.values(data.items).map((c) => c.at));
  const at = Math.max(now, last + 1);
  const changes: Item[] = [kind === "free" || kind === "tag-levels"
    ? { kind, key, on, at, value: on ? value : null }
    : { kind, key, on, at }];
  if (on && kind === "stars") changes.push({ kind: "hidden-events", key, on: false, at });
  if (on && kind === "hidden-events") changes.push({ kind: "stars", key, on: false, at });
  return changes;
}

/** Whether an event is hidden: you hid it, or it has a hidden type and none of your interests (shared/cases/hidden.json). */
export function isHidden(e: Pick<NowishEvent, "id" | "tags">, v: ProfileView): boolean {
  const sets: HideSets = { hiddenEvents: v.hiddenEvents, hiddenTags: v.hiddenTags, interests: v.interests, starredEvents: v.stars };
  return RULES.hides(e as NowishEvent, sets);
}
